import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import StickyQuoteBar from '../../components/feature/StickyQuoteBar';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function DSCRLoanBasicsPage() {
  const [ref1, inView1] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [ref2, inView2] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [ref3, inView3] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const howItWorksSteps = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Property Cash Flow Qualifies You',
      description: 'Use leases, appraisals, or market rent data to demonstrate income.',
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Flexible Terms',
      description: '30-year fixed rates (6.99–8.5% in 2026), LTV up to 80%, loans $75K–$5M+.',
    },
    {
      icon: 'ri-timer-flash-line',
      title: 'Fast & Simple',
      description: 'Pre-approval in 24 hours, close in 10–30 days.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Entity-Friendly',
      description: 'Borrow in LLCs or trusts with no personal guarantee.',
    },
  ];

  const requirements = [
    { icon: 'ri-bar-chart-box-line', label: 'Credit score', value: '620–680 (some accept 580+)' },
    { icon: 'ri-wallet-3-line', label: 'Down payment', value: '15–25% (75–85% LTV)' },
    { icon: 'ri-calculator-line', label: 'DSCR ratio', value: '1.0+ (no-ratio options available)' },
    { icon: 'ri-safe-line', label: 'Reserves', value: '3–12 months PITI' },
    { icon: 'ri-home-line', label: 'Property types', value: 'Rentals only (no owner-occupied)' },
    { icon: 'ri-file-list-3-line', label: 'Minimal docs', value: 'Appraisal, lease, insurance' },
  ];

  const benefits = [
    'No personal income verification',
    'Qualify on property cash flow only',
    'Scale portfolios without conventional limits',
    'Close 2–3× faster than banks',
    'Entity borrowing for asset protection',
    'Options for short-term rentals and low DSCR',
  ];

  const comparisons = [
    {
      type: 'Conventional',
      requirement: 'Needs personal income/docs',
      bgColor: 'bg-red-50',
      iconColor: 'text-red-600',
      icon: 'ri-close-circle-line',
    },
    {
      type: 'Hard Money',
      requirement: 'Higher rates (10–15%), shorter terms',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600',
      icon: 'ri-alert-line',
    },
    {
      type: 'Bank Statement',
      requirement: 'Requires personal bank statements',
      bgColor: 'bg-yellow-50',
      iconColor: 'text-yellow-600',
      icon: 'ri-file-chart-line',
    },
    {
      type: 'DSCR Loan',
      requirement: 'Pure rental investors win here',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      icon: 'ri-checkbox-circle-line',
    },
  ];

  const faqs = [
    {
      question: 'What is the minimum credit score for DSCR loans?',
      answer: 'Most DSCR lenders require a minimum credit score of 620–680. However, some specialized lenders accept scores as low as 580+, though you may face slightly higher interest rates or larger down payment requirements with lower credit scores.',
    },
    {
      question: 'Can DSCR loans be used for Airbnb and short-term rentals?',
      answer: 'Yes! DSCR loans are excellent for short-term rental properties like Airbnb and VRBO. Many lenders offer special STR (Short-Term Rental) programs that use projected rental income based on market analysis and comparable properties to calculate your DSCR ratio.',
    },
    {
      question: 'How fast do DSCR loans close?',
      answer: 'DSCR loans typically close in 10–30 days, which is 2–3× faster than traditional bank loans. You can receive pre-approval in as little as 24 hours, making DSCR loans ideal for competitive real estate markets where speed matters.',
    },
    {
      question: 'Do I need to provide tax returns or W-2s?',
      answer: 'No! This is the major advantage of DSCR loans. You qualify based solely on your property\'s rental income and cash flow potential—not your personal income. No tax returns, W-2s, pay stubs, or personal debt-to-income (DTI) calculations required.',
    },
    {
      question: 'What is a good DSCR ratio?',
      answer: 'A DSCR ratio of 1.0 means your property\'s rental income equals its debt obligations. Most lenders prefer 1.0 or higher, with 1.25+ receiving the best rates. Some lenders offer no-ratio DSCR loans for properties with lower cash flow if you provide a larger down payment.',
    },
    {
      question: 'Can I use a DSCR loan to purchase multiple properties?',
      answer: 'Absolutely! DSCR loans are designed for portfolio growth. Unlike conventional loans that limit you to 10 financed properties, DSCR loans have no cap. You can scale your rental portfolio indefinitely as long as each property meets the DSCR requirements.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <StickyQuoteBar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: 'url(https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/419447a3786951ed043db24ecd0edcf5.jpeg)'
            }}
          />
          
          {/* Dark Overlay for text contrast */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/75 via-slate-900/65 to-slate-950/80"></div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-5xl mx-auto"
            >
              <div className="inline-block px-5 py-2 bg-yellow-500 text-gray-900 rounded-full text-sm font-bold mb-6">
                <i className="ri-lightbulb-flash-line mr-2"></i>
                Cash Flow Financing Simplified
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 leading-tight">
                DSCR Loan Basics: Cash Flow Financing for Real Estate Investors
              </h1>
              <p className="text-lg md:text-xl lg:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed mb-10">
                Tired of banks demanding tax returns and W-2s? DSCR loans let you qualify based on your property's rental income—not your personal finances. Perfect for scaling portfolios, these non-QM loans offer fast closings, high LTV, and investor-friendly terms.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <Link
                  to="/apply"
                  className="w-full sm:w-auto px-10 py-4 bg-yellow-500 hover:bg-yellow-400 text-gray-900 text-lg font-bold rounded-xl shadow-2xl transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-file-text-line mr-2"></i>
                  Get Your Free DSCR Quote Now
                </Link>
                <a
                  href="tel:347-586-0671"
                  className="w-full sm:w-auto px-10 py-4 bg-white/10 hover:bg-white/20 text-white text-lg font-bold rounded-xl border-2 border-white/30 backdrop-blur-sm transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  347-586-0671
                </a>
              </div>
              <p className="text-sm text-gray-300">
                Get pre-qualified in minutes and fund your next rental in as little as 10–21 days
              </p>
            </motion.div>
          </div>
        </section>

        {/* What Is DSCR Section */}
        <section className="py-16 lg:py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div
                ref={ref1}
                initial={{ opacity: 0, x: -30 }}
                animate={inView1 ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8 }}
              >
                <div className="inline-block px-4 py-2 bg-yellow-500 text-gray-900 rounded-full text-sm font-bold mb-6">
                  <i className="ri-question-line mr-2"></i>
                  Core Definition
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  What Is a <span className="text-yellow-600">DSCR Loan?</span>
                </h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  A <strong>DSCR loan</strong> (Debt Service Coverage Ratio loan) is designed specifically for real estate investors. Lenders calculate your DSCR ratio—monthly rental income divided by debt payments (principal, interest, taxes, insurance, HOA).
                </p>
                <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                  If your property generates enough cash flow to cover the mortgage (typically DSCR ≥ 1.0–1.25), you qualify—<strong>without personal income verification</strong>.
                </p>

                {/* Example Card */}
                <div className="bg-white rounded-2xl p-8 shadow-xl border-l-4 border-yellow-500">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-yellow-100 rounded-xl">
                      <i className="ri-calculator-line text-2xl text-yellow-600"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Real Example</h3>
                  </div>
                  <div className="space-y-3 text-gray-700">
                    <p className="flex items-center justify-between text-lg">
                      <span>Monthly Rent:</span>
                      <strong className="text-green-600">$2,500</strong>
                    </p>
                    <p className="flex items-center justify-between text-lg">
                      <span>Debt Service:</span>
                      <strong className="text-gray-900">$2,000</strong>
                    </p>
                    <div className="border-t-2 border-gray-200 pt-3">
                      <p className="flex items-center justify-between text-xl">
                        <span className="font-bold">DSCR Ratio:</span>
                        <strong className="text-2xl text-yellow-600">1.25</strong>
                      </p>
                    </div>
                  </div>
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <p className="text-center text-green-600 font-bold text-lg flex items-center justify-center">
                      <i className="ri-checkbox-circle-fill mr-2 text-2xl"></i>
                      Approved — No W-2s, No Tax Returns, No Personal DTI Limits
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                ref={ref1}
                initial={{ opacity: 0, x: 30 }}
                animate={inView1 ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://static.readdy.ai/image/cd984ab7ba08a0f8b6f75adc7751930e/2f79e67b04a8f30e6628874c3f5fcdf3.jpeg"
                    alt="Understanding DSCR Loans"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/30 via-transparent to-transparent"></div>
                </div>

                {/* Floating Badge */}
                <div className="absolute -top-6 -right-6 bg-gradient-to-br from-yellow-500 to-yellow-600 text-gray-900 rounded-2xl shadow-2xl p-6 transform rotate-3">
                  <div className="text-center">
                    <div className="text-4xl font-bold mb-1">24hrs</div>
                    <p className="text-sm font-semibold">Pre-Approval</p>
                  </div>
                </div>

                {/* Stats Card */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-2xl p-6 border-t-4 border-yellow-500 max-w-xs">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 text-sm">LTV up to:</span>
                      <strong className="text-gray-900 text-lg">80%</strong>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 text-sm">Loan Range:</span>
                      <strong className="text-gray-900 text-lg">$75K–$5M+</strong>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 text-sm">Close in:</span>
                      <strong className="text-green-600 text-lg">10–30 Days</strong>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* How DSCR Loans Work */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              ref={ref2}
              initial={{ opacity: 0, y: 30 }}
              animate={inView2 ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="inline-block px-4 py-2 bg-yellow-500 text-gray-900 rounded-full text-sm font-bold mb-6">
                <i className="ri-flow-chart mr-2"></i>
                The Process
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                How DSCR Loans Work
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Simple, fast, and designed for real estate investors
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {howItWorksSteps.map((step, index) => (
                <motion.div
                  key={step.title}
                  ref={ref2}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView2 ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl mb-6">
                    <i className={`${step.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </motion.div>
              ))}
            </div>

            <motion.div
              ref={ref2}
              initial={{ opacity: 0, y: 20 }}
              animate={inView2 ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-12 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-2xl p-8 border-l-4 border-yellow-500"
            >
              <p className="text-lg text-gray-700 text-center">
                <strong>Ideal for:</strong> Single-family rentals, short-term (Airbnb/VRBO), multifamily (5+ units), and cash-out refis.
              </p>
            </motion.div>
          </div>
        </section>

        {/* DSCR Loan Requirements */}
        <section className="py-16 lg:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              ref={ref3}
              initial={{ opacity: 0, y: 30 }}
              animate={inView3 ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="inline-block px-4 py-2 bg-yellow-500 text-gray-900 rounded-full text-sm font-bold mb-6">
                <i className="ri-checkbox-line mr-2"></i>
                Qualification Criteria
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                DSCR Loan Requirements
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Straightforward criteria focused on property performance
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {requirements.map((req, index) => (
                <motion.div
                  key={req.label}
                  ref={ref3}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={inView3 ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-yellow-500 rounded-xl flex-shrink-0">
                      <i className={`${req.icon} text-2xl text-gray-900`}></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-2">{req.label}</h3>
                      <p className="text-gray-300">{req.value}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-yellow-500 text-gray-900 rounded-full text-sm font-bold mb-6">
                <i className="ri-star-line mr-2"></i>
                Why Investors Choose DSCR
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Key Benefits of DSCR Loans
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
                Rates average <strong>7–9%</strong> (2026 data from Visio Lending, Kiavi, Griffin Funding), with fees 1–3 points—competitive for non-QM.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start space-x-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border-l-4 border-green-500"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-green-500 rounded-full flex-shrink-0">
                    <i className="ri-check-line text-xl text-white"></i>
                  </div>
                  <p className="text-gray-900 font-semibold text-lg">{benefit}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* DSCR vs Other Loans */}
        <section className="py-16 lg:py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-yellow-500 text-gray-900 rounded-full text-sm font-bold mb-6">
                <i className="ri-contrast-2-line mr-2"></i>
                Loan Comparison
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                DSCR vs. Other Loan Types
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                See why DSCR loans win for pure rental investors
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {comparisons.map((comp, index) => (
                <motion.div
                  key={comp.type}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`${comp.bgColor} rounded-2xl p-8 border-2 ${comp.type === 'DSCR Loan' ? 'border-green-500' : 'border-transparent'} shadow-lg hover:shadow-xl transition-all duration-300`}
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <div className={`w-14 h-14 flex items-center justify-center ${comp.type === 'DSCR Loan' ? 'bg-green-500' : 'bg-white'} rounded-xl`}>
                      <i className={`${comp.icon} text-3xl ${comp.type === 'DSCR Loan' ? 'text-white' : comp.iconColor}`}></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">{comp.type}</h3>
                  </div>
                  <p className="text-lg text-gray-700">{comp.requirement}</p>
                  {comp.type === 'DSCR Loan' && (
                    <div className="mt-4 pt-4 border-t-2 border-green-500">
                      <p className="text-green-700 font-bold flex items-center">
                        <i className="ri-trophy-line mr-2 text-xl"></i>
                        Best for Real Estate Investors
                      </p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-yellow-500 text-gray-900 rounded-full text-sm font-bold mb-6">
                <i className="ri-question-answer-line mr-2"></i>
                Common Questions
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">
                Everything you need to know about DSCR loans
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors duration-300"
                  >
                    <h4 className="text-lg md:text-xl font-bold text-gray-900 pr-4 flex items-start">
                      <i className="ri-question-line text-yellow-600 mr-3 mt-1 flex-shrink-0"></i>
                      {faq.question}
                    </h4>
                    <i className={`ri-arrow-down-s-line text-2xl text-gray-600 flex-shrink-0 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`}></i>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'}`}
                  >
                    <div className="px-6 pb-6 pt-2 pl-16">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 lg:py-28 bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-500">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                Ready to Fund Your Next Deal?
              </h2>
              <p className="text-xl md:text-2xl text-gray-800 mb-10 max-w-3xl mx-auto">
                Calculate your DSCR and get personalized quotes from top nationwide lenders—free, no obligation.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/apply"
                  className="w-full sm:w-auto px-12 py-5 bg-gray-900 hover:bg-gray-800 text-white text-lg font-bold rounded-xl shadow-2xl transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-file-text-line mr-2"></i>
                  Get Free DSCR Quote
                </Link>
                <a
                  href="tel:347-586-0671"
                  className="w-full sm:w-auto px-12 py-5 bg-white hover:bg-gray-100 text-gray-900 text-lg font-bold rounded-xl border-2 border-gray-900 transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call 347-586-0671
                </a>
              </div>
              <p className="text-gray-800 mt-8 text-lg font-semibold">
                DSCR loans give real estate investors the edge. Start scaling today.
              </p>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
